/**
 * PHA-1158: fill ICD-10 codes on problems that still have none after the static lookup.
 *
 * The Review step of a regeneration seeds codes from a small static map (COPD, GERD, ...); any
 * other problem stayed blank, and a regeneration has no document parse to supply them. The
 * backend suggests a code per problem; this merges those suggestions back WITHOUT touching a
 * problem that has since gained a code (the clinician may have typed one while the request was
 * in flight). Every suggestion is flagged icd10_needs_review so it is confirmed, not trusted.
 */

export interface Icd10Suggestion {
  problem: string;
  icd10_code: string;
  icd10_description?: string;
}

const key = (name: unknown): string => String(name ?? '').trim().toLowerCase();

/** Names of problems that have no ICD-10 code yet, de-duplicated and non-empty. */
export function problemNamesMissingIcd10(problems: any[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of problems || []) {
    const name = String(p?.problem ?? '').trim();
    if (!name || String(p?.icd10_code ?? '').trim()) continue;
    if (seen.has(key(name))) continue;
    seen.add(key(name));
    out.push(name);
  }
  return out;
}

/** Apply suggestions to problems that are still uncoded; everything else is returned as is. */
export function applyIcd10Suggestions(problems: any[], suggestions: Icd10Suggestion[]): any[] {
  const byName = new Map<string, Icd10Suggestion>();
  for (const s of suggestions || []) {
    if (s && s.icd10_code && key(s.problem)) byName.set(key(s.problem), s);
  }
  if (byName.size === 0) return problems;
  return (problems || []).map((p) => {
    if (String(p?.icd10_code ?? '').trim()) return p;
    const s = byName.get(key(p?.problem));
    if (!s) return p;
    return {
      ...p,
      icd10_code: s.icd10_code,
      icd10_description: s.icd10_description || p.icd10_description,
      icd10_needs_review: true,
    };
  });
}
